<template>
  <div class="canasta-display">
    <!-- All canastas in a single row -->
    <div class="canasta-row">
      <!-- Clean Canastas -->
      <div
        v-for="(canasta, index) in cleanCanastas"
        :key="`clean-${index}`"
        class="canasta-stack clean"
        :class="{
          highlighted: highlightedIndex === index && highlightType === 'clean',
          visible: isStackVisible('clean', index),
          building: isBuildingStack('clean', index),
        }"
        :style="getStackStyle(index, 'clean')"
      >
        <!-- Building cards animation -->
        <template v-if="isBuildingStack('clean', index)">
          <div
            v-for="i in buildingCardCount"
            :key="`building-${i}`"
            class="stack-card building-card"
            :style="getBuildingCardStyle(i)"
          >
            <Card :card="null" :faceDown="true" size="small" />
          </div>
          <div
            v-if="buildingCardCount >= 4"
            class="stack-card top-card building-card"
            :style="getBuildingCardStyle(4)"
          >
            <Card :card="canasta.topCard" size="small" />
          </div>
        </template>

        <!-- Completed stack -->
        <template v-else-if="isStackVisible('clean', index)">
          <div
            v-for="i in 3"
            :key="`back-${i}`"
            class="stack-card"
            :style="getStackCardStyle(i)"
          >
            <Card :card="null" :faceDown="true" size="small" />
          </div>
          <div class="stack-card top-card" :style="getStackCardStyle(4)">
            <Card :card="canasta.topCard" size="small" />
          </div>
        </template>
      </div>

      <!-- Dirty/Wild Canastas -->
      <div
        v-for="(canasta, index) in dirtyWildCanastas"
        :key="`dirty-${index}`"
        class="canasta-stack"
        :class="{
          highlighted: highlightedIndex === index && highlightType === 'dirty',
          dirty: canasta.type === 'dirty',
          wild: canasta.type === 'wild',
          visible: isStackVisible('dirty', index),
          building: isBuildingStack('dirty', index),
        }"
        :style="getStackStyle(index, 'dirty')"
      >
        <!-- Building cards animation -->
        <template v-if="isBuildingStack('dirty', index)">
          <div
            v-for="i in buildingCardCount"
            :key="`building-${i}`"
            class="stack-card building-card"
            :style="getBuildingCardStyle(i)"
          >
            <Card :card="null" :faceDown="true" size="small" />
          </div>
          <div
            v-if="buildingCardCount >= 4"
            class="stack-card top-card building-card"
            :style="getBuildingCardStyle(4)"
          >
            <Card :card="canasta.topCard" size="small" />
          </div>
        </template>

        <!-- Completed stack -->
        <template v-else-if="isStackVisible('dirty', index)">
          <div
            v-for="i in 3"
            :key="`back-${i}`"
            class="stack-card"
            :style="getStackCardStyle(i)"
          >
            <Card :card="null" :faceDown="true" size="small" />
          </div>
          <div class="stack-card top-card" :style="getStackCardStyle(4)">
            <Card :card="canasta.topCard" size="small" />
          </div>
        </template>
      </div>

      <!-- Empty state -->
      <div v-if="cleanCanastas.length === 0 && dirtyWildCanastas.length === 0" class="no-canastas">
        No canastas
      </div>
    </div>

    <!-- Total -->
    <div class="canasta-total">
      <span class="total-value">{{ displayTotal.toLocaleString() }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoundEndAnimations } from '../../composables/useRoundEndAnimations'
import { useSounds } from '../../composables/useSounds'
import Card from '../cards/Card.vue'

interface CardData {
  rank: string
  suit: string
}

interface CanastaData {
  type: 'clean' | 'dirty' | 'wild'
  topCard: CardData
  points: number
}

interface Props {
  team: 'A' | 'B'
  teamName: string
  canastas: {
    natural: number
    mixed: number
    wild: number
  }
  isAnimating?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isAnimating: false,
})

const emit = defineEmits<{
  countComplete: [cleanTotal: number, dirtyWildTotal: number]
}>()

const { generateNaturalPosition } = useRoundEndAnimations()
const { playPileHighlight, playPileAppear } = useSounds()

// Animation state
const highlightedIndex = ref(-1)
const highlightType = ref<'clean' | 'dirty' | null>(null)
const displayTotal = ref(0)

// Sequential building animation state
const completedCleanCount = ref(0)
const completedDirtyCount = ref(0)
const currentBuildingType = ref<'clean' | 'dirty' | null>(null)
const currentBuildingIndex = ref(-1)
const buildingCardCount = ref(0)

// Random positions for natural dealing effect (computed once)
const stackPositions = ref<Map<string, { rotation: number; offsetX: number; offsetY: number }>>(
  new Map()
)

// Generate a representative top card for a canasta type
function getTopCard(type: 'clean' | 'dirty' | 'wild', index: number): CardData {
  if (type === 'wild') {
    // Wild canastas show Joker as top card
    return { rank: 'JOKER', suit: 'JOKER' }
  }
  // Natural and mixed canastas use different ranks for variety
  const ranks = ['K', 'Q', 'J', '10', '9', '8', '7']
  const suits = type === 'clean' ? ['hearts', 'diamonds'] : ['clubs', 'spades']
  return {
    rank: ranks[index % ranks.length],
    suit: suits[index % suits.length],
  }
}

// Build canasta arrays
const cleanCanastas = computed<CanastaData[]>(() => {
  return Array.from({ length: props.canastas.natural }, (_, i) => ({
    type: 'clean' as const,
    topCard: getTopCard('clean', i),
    points: 500,
  }))
})

const dirtyWildCanastas = computed<CanastaData[]>(() => {
  const dirty = Array.from({ length: props.canastas.mixed }, (_, i) => ({
    type: 'dirty' as const,
    topCard: getTopCard('dirty', i),
    points: 300,
  }))

  const wild = Array.from({ length: props.canastas.wild }, (_, i) => ({
    type: 'wild' as const,
    topCard: getTopCard('wild', i),
    points: 1000,
  }))

  return [...dirty, ...wild]
})

// Get stack style with natural positioning
function getStackStyle(index: number, type: 'clean' | 'dirty'): Record<string, string> {
  const key = `${type}-${index}`

  // Generate position once and cache
  if (!stackPositions.value.has(key)) {
    const pos = generateNaturalPosition(0, 0, index, false)
    stackPositions.value.set(key, {
      rotation: pos.rotation,
      offsetX: pos.x,
      offsetY: pos.y,
    })
  }

  const pos = stackPositions.value.get(key)!

  return {
    transform: `rotate(${pos.rotation}deg) translate(${pos.offsetX}px, ${pos.offsetY}px)`,
    animationDelay: `${index * 0.12}s`, // Brisk stagger timing
  }
}

// Get card style within stack - 3D offset effect
function getStackCardStyle(cardIdx: number): Record<string, string> {
  const offset = (cardIdx - 1) * 2 // Stack cards with slight Y offset
  return {
    position: 'absolute',
    top: `${-offset}px`,
    left: '0',
    zIndex: String(cardIdx),
  }
}

// Get style for building cards (fly-in animation)
function getBuildingCardStyle(cardIdx: number): Record<string, string> {
  const offset = (cardIdx - 1) * 2
  return {
    position: 'absolute',
    top: `${-offset}px`,
    left: '0',
    zIndex: String(cardIdx),
  }
}

// Check if a stack should be visible (completed building)
function isStackVisible(type: 'clean' | 'dirty', index: number): boolean {
  // Ensure mutual exclusivity - never show as "visible" while building
  if (isBuildingStack(type, index)) {
    return false
  }
  if (type === 'clean') {
    return index < completedCleanCount.value
  } else {
    return index < completedDirtyCount.value
  }
}

// Check if a stack is currently being built
function isBuildingStack(type: 'clean' | 'dirty', index: number): boolean {
  return currentBuildingType.value === type && currentBuildingIndex.value === index
}

// Calculate total
const totalBonus = computed(() => {
  return (
    props.canastas.natural * 500 + props.canastas.mixed * 300 + props.canastas.wild * 1000
  )
})

// Animate building a single canasta stack sequentially
async function buildSingleCanasta(): Promise<void> {
  const CARDS_PER_STACK = 4 // Visual cards in the stack (3 back + 1 top)
  const CARD_DEAL_DELAY = 80 // ms between each card dealing

  playPileAppear()

  // Deal cards one by one
  for (let i = 1; i <= CARDS_PER_STACK; i++) {
    buildingCardCount.value = i
    await delay(CARD_DEAL_DELAY)
  }

  // Brief pause after stack completes
  await delay(150)
}

// Sequential building animation
async function animateSequentialBuilding(): Promise<void> {
  const cleanCount = cleanCanastas.value.length
  const dirtyCount = dirtyWildCanastas.value.length

  // Reset state
  completedCleanCount.value = 0
  completedDirtyCount.value = 0
  currentBuildingType.value = null
  currentBuildingIndex.value = -1
  buildingCardCount.value = 0
  displayTotal.value = 0

  // Build clean canastas first
  for (let i = 0; i < cleanCount; i++) {
    currentBuildingType.value = 'clean'
    currentBuildingIndex.value = i
    buildingCardCount.value = 0

    await buildSingleCanasta()

    // Mark as complete, move to next
    completedCleanCount.value = i + 1
    currentBuildingType.value = null
    currentBuildingIndex.value = -1
    buildingCardCount.value = 0

    // Update running total
    displayTotal.value += 500
  }

  // Then build dirty/wild canastas
  for (let i = 0; i < dirtyCount; i++) {
    currentBuildingType.value = 'dirty'
    currentBuildingIndex.value = i
    buildingCardCount.value = 0

    await buildSingleCanasta()

    // Mark as complete
    completedDirtyCount.value = i + 1
    currentBuildingType.value = null
    currentBuildingIndex.value = -1
    buildingCardCount.value = 0

    // Update running total (300 for dirty, 1000 for wild)
    const canasta = dirtyWildCanastas.value[i]
    displayTotal.value += canasta.points
  }

  // Brief pause before emitting complete
  await delay(200)

  emit(
    'countComplete',
    props.canastas.natural * 500,
    props.canastas.mixed * 300 + props.canastas.wild * 1000
  )
}

// Watch for animation trigger
watch(
  () => props.isAnimating,
  (animating) => {
    if (animating) {
      animateSequentialBuilding()
    }
  },
  { immediate: true }
)

// Initialize display total (non-animated state)
onMounted(() => {
  if (!props.isAnimating) {
    displayTotal.value = totalBonus.value
    completedCleanCount.value = cleanCanastas.value.length
    completedDirtyCount.value = dirtyWildCanastas.value.length
  }
})

// Delay helper
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Animate canasta counting with highlight effect (like 100pt piles)
async function animateCanastaCount(voiceDuration: number): Promise<void> {
  const cleanCount = cleanCanastas.value.length
  const dirtyWildCount = dirtyWildCanastas.value.length
  const totalCount = cleanCount + dirtyWildCount

  if (totalCount === 0) {
    return
  }

  // Initial delay to sync with voice startup latency (~700ms)
  // Web Speech API has startup delay before audio actually plays
  const voiceStartupDelay = 700
  await delay(voiceStartupDelay)

  // Use remaining time for the actual counting intervals (full duration, no reduction)
  const remainingDuration = Math.max(voiceDuration - voiceStartupDelay, totalCount * 100)
  const interval = remainingDuration / totalCount

  // Animate clean canastas first
  for (let i = 0; i < cleanCount; i++) {
    highlightedIndex.value = i
    highlightType.value = 'clean'
    playPileHighlight()

    await delay(interval)
    highlightedIndex.value = -1
  }

  // Then dirty/wild canastas
  for (let i = 0; i < dirtyWildCount; i++) {
    highlightedIndex.value = i
    highlightType.value = 'dirty'
    playPileHighlight()

    await delay(interval)
    highlightedIndex.value = -1
  }

  highlightType.value = null
}

// Expose animation method for parent component
defineExpose({
  animateCanastaCount,
})
</script>

<style scoped>
/* Minimal container - no heavy borders */
.canasta-display {
  padding: 1rem;
}

/* Single row of all canastas */
.canasta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  align-items: flex-end;
  min-height: 120px;
  padding: 0.5rem;
}

/* Individual canasta stack */
.canasta-stack {
  position: relative;
  width: 65px;
  height: 95px;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.2s ease-out, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Show stack when visible or building */
.canasta-stack.visible,
.canasta-stack.building {
  opacity: 1;
  transform: scale(1);
}

/* Type-based glows - subtle color coding */
.canasta-stack.clean {
  filter: drop-shadow(0 0 8px rgba(34, 197, 94, 0.5));
}

.canasta-stack.dirty {
  filter: drop-shadow(0 0 8px rgba(148, 163, 184, 0.4));
}

.canasta-stack.wild {
  filter: drop-shadow(0 0 10px rgba(139, 92, 246, 0.6));
}

@keyframes stackAppear {
  from {
    opacity: 0;
    transform: scale(0.6) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.canasta-stack.highlighted {
  animation: countingPulse 0.3s ease-out;
}

@keyframes countingPulse {
  50% {
    transform: scale(1.1);
    filter: brightness(1.2) drop-shadow(0 0 15px rgba(232, 197, 71, 0.6));
  }
}

/* Card positioning within stack */
.stack-card {
  position: absolute;
  width: 58px;
  height: 82px;
}

.stack-card.top-card {
  z-index: 10;
}

/* Building card fly-in animation */
.stack-card.building-card {
  animation: cardFlyIn 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes cardFlyIn {
  from {
    opacity: 0;
    transform: translateX(-60px) translateY(-30px) rotate(-15deg) scale(0.7);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0) rotate(0) scale(1);
  }
}

/* Empty state */
.no-canastas {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
  font-style: italic;
}

/* Total - simple and clean */
.canasta-total {
  text-align: center;
  margin-top: 1.5rem;
}

.total-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e8c547;
}
</style>
